import io from 'socket.io-client';
import axiosInstance from "./axiosInstance.js";
import { getToken } from 'utils/auth';


export const socket = io(process.env.REACT_APP_API_BASE_URL || 'https://academy.innovatorsforge.com', {
  withCredentials: true,
});

// Initialize socket.io client LOCALHOST
// export const socket = io('http://localhost:3005', {
//   withCredentials: true,
// });

const API_MESSAGES = '/api/messages';


// 🔐 Add token to every request
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default axiosInstance;

//Fetch the logged-in users profil
export const getUserProfile = async () => {
  try {
    const response = await axiosInstance.get('/api/users/profile');
    return response.data;
  } catch (error) {
    console.error("Error fetching user profile", error);
    throw error;
  }
};

// Create a new reservation
export const createReservation = async (reservationData) => {
  try {
    const response = await axiosInstance.post(`/api/reservations`, reservationData);
    return response.data;
  } catch (error) {
    console.error("Error creating reservation:", error);
    throw error;
  }
};

export const fetchReservationSummary = async () => {
  try {
    // This calls the GET /api/reservations/summary endpoint
    const response = await axiosInstance.get('/api/reservations/summary');
    return response.data;
  } catch (error) {
    // This will catch errors like 401 Unauthorized or 500 Server Error
    console.error("Error fetching reservation summary:", error);
    throw error; // Re-throw the error so the component can handle it
  }
};

//  Fetch my reservations
export const fetchMyReservations = async () => {
  try {
    const res = await axiosInstance.get(`/api/reservations/mine`);
    return res.data;
  } catch (error) {
    console.error('Error fetching my reservations:', error);
    throw error;
  }
};

export const fetchMentorClassrooms = async () => {
    const token = getToken();
    if (!token) throw new Error("Authentication token is missing.");

    const res = await axiosInstance.get(`/api/classrooms/mentor`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    return res.data;
};

// Update a classroom
export const updateClassroom = async ({ id, ...classroomData }) => {
  try {
    const response = await axiosInstance.put(`/api/classrooms/${id}`, classroomData);
    return response.data;
  } catch (error) {
    console.error("Error updating classroom:", error);
    throw error;
  }
};

// Delete a classroom (and all its related teams on the backend)
export const deleteClassroom = async (classroomId) => {
  try {
    const response = await axiosInstance.delete(`/api/classrooms/${classroomId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting classroom:", error);
    throw error;
  }
};
// This replaces fetchTeamsByMentor, fetchApprovalRequests, and the loop of fetchTeamStepStatus
export const fetchMentorDashboardData = async () => {
  try {
    const response = await axiosInstance.get('/api/mentors/dashboard-data');
    return response.data;
  } catch (error) {
    console.error("Error fetching mentor dashboard data:", error);
    throw error;
  }
};

// Fetch the logged-in student's profile
export const getStudentProfile = async () => {
  try {
    const response = await axiosInstance.get('/api/students/profile');
    return response.data;
  } catch (error) {
    console.error("Error fetching student profile", error);
    throw error;
  }
};

//Search for users excluding mentors
export const searchUsersMinus = async (username) => {
  try {
    const response = await axiosInstance.get('/api/users/search-exclude-mentors', {
      params: { username } 
    });
    return response.data;
  } catch (error) {
    console.error("Error searching users excluding mentors:", error);
    return [];
  }
};

// Change user's password
export const updatePassword = async (userId, { oldPassword, newPassword }) => {
  try {
      const response = await axiosInstance.put(`/api/users/${userId}/update-password`, { oldPassword, newPassword });
      return response.data;
  } catch (error) {
      if (error.response) {
          // Return error from server
          throw new Error(error.response.data.message || "Failed to update password.");
      }
      console.error("Error updating password:", error);
      throw new Error("Failed to update password.");
  }
};

// Toggle Two-Factor Authentication
export const toggleTwoFactorAuth = async (userId, { isEnabled }) => {
  try {
    const response = await axiosInstance.put(`/api/users/${userId}/two-factor-auth`, { isEnabled });
    return response.data;
  } catch (error) {
    console.error("Error toggling Two-Factor Authentication:", error);
    throw error;
  }
};

// Search users by school
export const searchUsersBySchool = async (username, excludeUserId) => {
  try {
    const response = await axiosInstance.get('/api/users/search', {
      params: { username, excludeUserId }
    });
    return response.data;
  } catch (error) {
    console.error("Error searching users by school:", error);
    return [];
  }
};

// Upload user's profile picture
export const uploadProfilePic = async (formData) => {
  try {
    const response = await axiosInstance.post('/api/users/upload-profile-pic', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading profile picture:", error);
    throw error;
  }
};



//Fetch team-members for logged-in user
export const fetchTeamMembers = async (teamId) => {
  try {
    const response = await axiosInstance.get(`/api/teams/${teamId}/members`);
    return response.data;
  } catch (error) {
    console.error("Error fetching team members:", error.response ? error.response.data : error.message);
    throw error;
  }
};

// Update a team member's role (teamRole)
export const updateTeamMember = async (teamId, memberId, teamRole) => {
  try {
    const response = await axiosInstance.put(`/api/teams/${teamId}/members/${memberId}`, { teamRole });
    return response.data;
  } catch (error) {
    console.error("Error updating team member role:", error);
    throw error;
  }
};

// Remove a team member from a team
export const removeTeamMember = async (teamId, memberId) => {
  try {
    const response = await axiosInstance.delete(`/api/teams/${teamId}/members/${memberId}`);
    return response.data;
  } catch (error) {
    console.error("Error removing team member:", error);
    throw error;
  }
};

// Fetch a specific user by ID
export const fetchUserById = async (userId) => {
  try {
    const response = await axiosInstance.get(`/api/users/${userId}`);
    return response.data;
  } catch (error) {
    console.log("Error fetching user details:", error);
    return null;
  }
};

export const fetchUsersBySchool = async (schoolName) => {
  try {
    const response = await axiosInstance.get(`/api/users/school/${encodeURIComponent(schoolName)}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching users by school:", error);
    throw error;
  }
};

export const getChatList = async () => {
  try {
    const response = await axiosInstance.get(`${API_MESSAGES}/chats`);
    return response.data;
  } catch (error) {
    console.error("Error fetching chat list:", error);
    throw error;
  }
};

/**
 * UPDATED: Fetches the message history for a 1-on-1 chat.
 */
export const getMessagesForUser = async (chatPartnerId) => {
  try {
    const response = await axiosInstance.get(`${API_MESSAGES}/users/${chatPartnerId}/messages`);
    return response.data;
  } catch (error) {
    console.error("Error fetching user messages:", error);
    throw error;
  }
};

/**
 * UPDATED: Fetches the message history for a team chat.
 */
export const getMessagesForTeam = async (teamId) => {
  try {
    const response = await axiosInstance.get(`${API_MESSAGES}/teams/${teamId}/messages`);
    return response.data;
  } catch (error) {
    console.error("Error fetching team messages:", error);
    throw error;
  }
};


//Check whether the payload being sent is logical
export const sendMessage = async ({ isTeamChat, messageData }) => {
    try {
        // Construct the correct endpoint URL with '/text' for text messages.
        const endpoint = isTeamChat 
            ? `${API_MESSAGES}/teams/${messageData.teamId}/messages/text` 
            : `${API_MESSAGES}/users/${messageData.receiverId}/messages/text`;
        const response = await axiosInstance.post(endpoint, messageData);
        
        return response.data;
    } catch (error) {
        console.error("Error sending message:", error);
        throw error;
    }
};

export const markMessagesAsRead = async ({ messageIds, chatPartnerId }) => {
  try {
    const response = await axiosInstance.post(`${API_MESSAGES}/mark-read`, {
      messageIds,
      chatPartnerId
    });
    return response.data;
  } catch (error) {
    console.error("Error marking messages as read:", error);
    throw error;
  }
};

export const markTeamMessagesAsRead = async ({ messageIds, teamId }) => {
    try {
        const response = await axiosInstance.post(`${API_MESSAGES}/teams/${teamId}/mark-read`, {
            messageIds,
        });
        return response.data;
    } catch (error) {
        console.error("Error marking team messages as read:", error);
        throw error;
    }
};

export const deleteMessage = async ({ messageId, teamId }) => {
  try {
    const endpoint = teamId 
      ? `${API_MESSAGES}/teams/messages/${messageId}` 
      : `${API_MESSAGES}/messages/${messageId}`;
      
    const response = await axiosInstance.delete(endpoint);
    return response.data;
  } catch (error) {
    console.error("Error deleting message:", error);
    throw error;
  }
};

/**
 * UPDATED: Clears chat history for a 1-on-1 chat.
 */
export const clearChatHistory = async (chatPartnerId) => {
  try {
    const response = await axiosInstance.post(`${API_MESSAGES}/users/${chatPartnerId}/clear-history`);
    return response.data;
  } catch (error) {
    console.error("Error clearing chat history:", error);
    throw error;
  }
};

/**
 * UPDATED: Clears chat history for a team.
 */
export const clearTeamChatHistory = async (teamId) => {
  try {
    const response = await axiosInstance.post(`${API_MESSAGES}/teams/${teamId}/clear-history`);
    return response.data;
  } catch (error) {
    console.error("Error clearing team chat history:", error);
    throw error;
  }
};

export const uploadImageFile = async ({ formData, isTeamChat, chatId }) => {
    try {
        const endpoint = isTeamChat 
            ? `${API_MESSAGES}/teams/${chatId}/messages/image` 
            : `${API_MESSAGES}/users/${chatId}/messages/image`;

        const response = await axiosInstance.post(endpoint, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (error) {
        console.error("Error uploading image file:", error);
        throw error;
    }
};

export const uploadDocumentFile = async ({ formData, isTeamChat, chatId }) => {
    try {
        const endpoint = isTeamChat 
            ? `${API_MESSAGES}/teams/${chatId}/messages/document` 
            : `${API_MESSAGES}/users/${chatId}/messages/document`;

        const response = await axiosInstance.post(endpoint, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (error) {
        console.error("Error uploading document file:", error);
        throw error;
    }
};

export const uploadModelFile = async ({ formData, isTeamChat, chatId }) => {
    try {
        const endpoint = isTeamChat 
            ? `${API_MESSAGES}/teams/${chatId}/messages/model` 
            : `${API_MESSAGES}/users/${chatId}/messages/model`;

        const response = await axiosInstance.post(endpoint, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (error) {
        console.error("Error uploading model file:", error);
        throw error;
    }
};


// Team ranking
export const fetchTeamRankings = async () => {
  try {
    const response = await axiosInstance.get(`/api/teams/ranked`);
    return response.data;
  } catch (error) {
    console.error("Error fetching rankings:", error);
    return [];
  }
};

//Achievements
export const fetchTopAchievements = async () => {
  try {
    const response = await axiosInstance.get(`/api/teams/achievements`);
    return response.data;
  } catch (error) {
    console.error(" Error fetching top achievements:", error);
    return [];
  }
};


// Add members to a team
export const addMembersToTeam = async (teamId, members) => {
  try {
    const response = await axiosInstance.post(`/api/teams/${teamId}/add-members`, { members });
    return response.data;
  } catch (error) {
    console.error("Error adding members to team:", error);
    throw error;
  }
};

// Check if team name exists
export const checkTeamName = async (name) => {
  try {
    const response = await axiosInstance.get('/api/teams/checkTeamName', { params: { name } });
    return response.data;
  } catch (error) {
    console.error("Error checking team name:", error);
    throw error;
  }
};


// Fetch tasks for teams
export const fetchUniversalTasks = async () => {
  try {
    const response = await axiosInstance.get(`/api/tasks`);
    return response.data;
  } catch (error) {
    console.error("Error fetching tasks:", error);
    return [];
  }
};

// Complete a task for a team
export const completeTaskForTeam = async (taskId, teamId) => {
  try {
    const response = await axiosInstance.post(`/api/tasks/complete`, { taskId, teamId });
    return response.data;
  } catch (error) {
    console.error("Error completing task:", error);
    throw error;
  }
};

// Fetch team specific task completion status
export const fetchTeamTaskStatuses = async (teamId) => {
  try {
    const response = await axiosInstance.get(`/api/teams/${teamId}/task-status`);
    return response.data;
  } catch (error) {
    console.error("Error fetching team status", error);
    return [];
  }
};



// Fetch all POAP descriptions
export const fetchPoapDescriptions = async () => {
  try {
    const response = await axiosInstance.get(`/api/poap-descriptions`);
    return response.data;
  } catch (error) {
    console.error("Error fetching POAP descriptions:", error);
    return [];
  }
};

// Fetch a specific POAP description by ID
export const fetchPoapDescriptionById = async (id) => {
  try {
    const response = await axiosInstance.get(`/api/poap-descriptions/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching POAP description:", error);
    return null;
  }
};

// Fetch TeamStep Statuses for a specific POAP description
export const fetchTeamStepStatus = async (teamId, descriptionId) => {
    try {
        // Use the POAP-specific endpoint to get steps status for a particular POAP
        const response = await axiosInstance.get(`/api/poap-descriptions/teams/${teamId}/poap-descriptions/${descriptionId}/steps-status`);
        return response.data;
    } catch (error) {
        console.error("Error fetching team step statuses", error);
        throw error;
    }
};

//Complete TeamSteps
export const completeStep = async (teamId, stepId, descriptionId) => {
  try {
    const response = await axiosInstance.post(`/api/poap-descriptions/poap/completeStep/${teamId}/${stepId}`, { descriptionId });
    return response.data;
  } catch (error) {
    console.error("Error completing step:", error);
    throw error;
  }
};

// Fetch team points info
export const fetchTeamPointsInfo = async (teamId, descriptionId) => {
  try {
    const response = await axiosInstance.get(`/api/poap-descriptions/points-info/${teamId}/${descriptionId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching team points info:", error);
    throw error;
  }
};

//Fetch current DescriptionId
export const fetchCurrentDescription = async (teamId) => {
  try {
    const response = await axiosInstance.get(`/api/poap-descriptions/poap/currentDescription/${teamId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching current description:", error);
    throw error;
  }
};

// Subscribe to newsletter
export const subscribeToNewsletter = async (email) => {
  try {
    const response = await axiosInstance.post('/api/subscribe', { email });
    return response.data;
  } catch (error) {
    console.error("Error subscribing to newsletter:", error);
    throw error;
  }
};

export const fetchSpecificStepStatus = async (teamId, descriptionId, poapStepId) => {
    try {
      const response = await axiosInstance.get(`/api/poap-descriptions/teams/${teamId}/poap-descriptions/${descriptionId}/steps/${poapStepId}/status`);
      return response.data;
    } catch (error) {
      console.error("Error fetching specific description", error)
      throw error
    }
    
};

export const fetchProjectStatus = async (teamId, descriptionId) => {
  try {
      const response = await axiosInstance.get(`/api/poap-descriptions/teams/${teamId}/poap-descriptions/${descriptionId}/status`);
      return response.data;
  } catch (error) {
      console.error("Error fetching project status", error);
      throw error;
  }
};

// Fetch all tasks
export const fetchTasks = async () => {
  try {
    const response = await axiosInstance.get(`/api/tasks`);
    return response.data;
  } catch (error) {
    console.error("Error fetching tasks:", error);
    return [];
  }
};

// Fetch tasks for a specific MintLink and Team
export const fetchTasksByMintLinkAndTeam = async (mintLinkId, teamId) => {
  try {
    const response = await axiosInstance.get(`/api/tasks/${mintLinkId}/${teamId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching tasks:", error);
    return [];
  }
};

// Fetch all club points
export const fetchClubPoints = async () => {
  try {
    const response = await axiosInstance.get(`/api/points`);
    return response.data;
  } catch (error) {
    console.error("Error fetching club points:", error);
    return [];
  }
};

// Fetch club points by team ID
export const fetchClubPointsByTeam = async (teamId) => {
  try {
    const response = await axiosInstance.get(`/api/points/team/${teamId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching club points by team ID:", error);
    return [];
  }
};

// Create a new club point
export const createClubPoint = async (clubPointData) => {
  try {
    const response = await axiosInstance.post(`/api/points`, clubPointData);
    return response.data;
  } catch (error) {
    console.error("Error creating club point:", error);
    throw error;
  }
};

// Update a club point
export const updateClubPoint = async (clubPointId, clubPointData) => {
  try {
    const response = await axiosInstance.put(`/api/points/${clubPointId}`, clubPointData);
    return response.data;
  } catch (error) {
    console.error("Error updating club point:", error);
    throw error;
  }
};

// Delete a club point
export const deleteClubPoint = async (clubPointId) => {
  try {
    const response = await axiosInstance.delete(`/api/points/${clubPointId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting club point:", error);
    throw error;
  }
};


//Email Verification
export const handleVerification = async (code) => {
  try {
    const response = await axiosInstance.get(`/api/auth/verify-email`, {
      params: { code }
    });
    return response.data
  } catch (error) {
    console.error("Error verifying email", error);
    throw error;
  }
};



//Resend Verification code
export const resendVerificationCode = async ({ email }) => {
  try {
    const response = await axiosInstance.post(`/api/auth/resend-verification`, {email});
    return response.data;
  } catch (error) {
    console.error("Error resending verification code", error);
    throw error;
  }  
};

//ADMIN FUNCTIONS
// Create a new user
export const createUser = async (userData) => {
  try {
    const response = await axiosInstance.post('/api/users', userData);
    return response.data;
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

// Fetch Users
export const fetchUsers = async () => {
  try {
    const response = await axiosInstance.get('/api/users');
    return response.data.map(user => ({
      id: user.id,
      name: [`${user.firstName} ${user.lastName}`, user.username, user.imageUrl],
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      username: user.username,
      phoneNumber: user.phoneNumber,
      enrollmentYear: user.enrollmentYear,
      country: user.country,
      status: user.status,
      role: user.role,
      teamId: user.teamId,
      team: user.team ? user.team.name : "No Team",
      teamRole: user.teamRole,
      school: user.school,
      mentorId: user.mentorId,
      aboutMe: user.aboutMe,
      createdAt: user.createdAt, // Added missing createdAt field
      updatedAt: user.updatedAt, // Added updatedAt field for completeness
      lastSeen: user.lastSeen,   // Added lastSeen field
    }));
  } catch (error) {
    console.log("Error fetching users:", error);
    return [];
  }
};



// Update an existing user
export const updateUser = async (userId, userData) => {
  try {
    const response = await axiosInstance.put(`/api/users/update/${userId}`, userData);
    return response.data;
  } catch (error) {
    if (error.response) {
      // Handle server errors (e.g., validation issues)
      throw new Error(error.response.data.message || "Failed to update user.");
    } else {
      console.error("Error updating user:", error);
      throw new Error("Failed to update user.");
    }
  }
};

// Delete a user
export const deleteUser = async (userId) => {
  try {
    const response = await axiosInstance.delete(`/api/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
};

// Fetch all teams
export const fetchTeams = async () => {
  try {
    const response = await axiosInstance.get(`/api/teams`);
    // Ensure the response data is in the expected format
    if (Array.isArray(response.data)) {
      return response.data.map(team => ({
        id: team.id,
        name: team.name,
        description: team.description,
        schoolName: team.schoolName,
        points: team.points,
        currentPoapLevel: team.currentLevels
      }));
    } else {
      return [];
    }
  } catch (error) {
    console.error("Error fetching teams:", error);
    return [];
  }
};

//Get Mentor's teams
export const fetchTeamsByMentor = async (mentorId) => {
  try {
    const response = await axiosInstance.get(`/api/teams/mentor/${mentorId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching teams associated with this mentor:", error);
    throw error;
  }
};

// Create a new team
export const createTeam = async (teamData) => {
  try {
    const response = await axiosInstance.post('/api/teams', teamData);
    return response.data;
  } catch (error) {
    console.error("Error creating team:", error);
    throw error;
  }
};

// Update an existing team
export const updateTeam = async (teamId, teamData) => {
  try {
    const response = await axiosInstance.put(`/api/teams/${teamId}`, teamData);
    return response.data;
  } catch (error) {
    console.error("Error updating team:", error);
    throw error;
  }
};

// Delete a team
export const deleteTeam = async (teamId) => {
  try {
    const response = await axiosInstance.delete(`/api/teams/${teamId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting team:", error);
    throw error;
  }
};


// Issue POAPs to teams via mint links
export const issuePoapsToTeam = async (teamId) => {
  try {
    const response = await axiosInstance.post(`/api/teams/${teamId}/issue-poaps`);
    return response.data;
  } catch (error) {
    console.error("Error issuing POAPs to team:", error);
    throw error;
  }
};


// Create a new POAP description
export const createPoapDescription = async (poapData) => {
  try {
    const response = await axiosInstance.post(`/api/poap-descriptions`, poapData);
    return response.data;
  } catch (error) {
    console.error("Error creating POAP description:", error);
    throw error;
  }
};

// Update an existing POAP description
export const updatePoapDescription = async (id, poapData) => {
  try {
    const response = await axiosInstance.put(`/api/poap-descriptions/${id}`, poapData);
    return response.data;
  } catch (error) {
    console.error("Error updating POAP description:", error);
    throw error;
  }
};

// Delete a POAP description
export const deletePoapDescription = async (id) => {
  try {
    const response = await axiosInstance.delete(`/api/poap-descriptions/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting POAP description:", error);
    throw error;
  }
};

// Complete team step
export const completeTeamStep = async (teamId, stepId, descriptionId) => {
  try {
    const response = await axiosInstance.post(`/api/poap-descriptions/teams/${teamId}/steps/${stepId}/complete`, { descriptionId });
    return response.data;
  } catch (error) {
    console.error("Error completing team step:", error);
    throw error;
  }
};

// Fetch notifications for a specific user
export const fetchNotifications = async (userId) => {
  try {
    const response = await axiosInstance.get(`/api/notifications/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching notifications:", error);
    return [];
  }
};

// Mark a notification as read
export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await axiosInstance.put(`/api/notifications/${notificationId}/read`);
    return response.data;
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
};

// Create a new notification (This can be used if needed)
export const createNotification = async (notificationData) => {
  try {
    const response = await axiosInstance.post('/api/notifications', notificationData);
    return response.data;
  } catch (error) {
    console.error("Error creating notification:", error);
    throw error;
  }
};

// Forgot Password
export const forgotPassword = async (email) => {
  try {
    const response = await axiosInstance.post('/api/users/forgot-password', { email });
    return response.data;
  } catch (error) {
    console.error("Error requesting password reset:", error);
    throw error;
  }
};

// Reset Password
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await axiosInstance.post('/api/users/reset-password', { token, newPassword });
    return response.data;
  } catch (error) {
    console.error("Error resetting password:", error);
    throw error;
  }
};

// Validate Reset Password Token
export const validateResetToken = async (token) => {
  try {
    const response = await axiosInstance.get(`/api/users/validate-reset-token/${token}`);
    return response.data;
  } catch (error) {
    console.error("Error validating reset token:", error);
    throw error;
  }
};

export const fetchMentorBySchool = async (school) => {
  try {
    const response = await axiosInstance.get(`/api/teams/schools/${encodeURIComponent(school)}/mentor`);
    return response.data.mentorId;
  } catch (error) {
    console.error("Error fetching mentor by school:", error);
    throw error;
  }
};

export const fetchUsersAssignedToMentor = async (mentorId) => {
  try {
    const response = await axiosInstance.get(`/api/users/assigned-to-mentor/${mentorId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching users assigned to mentor", error);
    throw error;
  }
};


// Approval requests
export const fetchApprovalRequests = async () => {
  try {
    const response = await axiosInstance.get('/api/approvals/mentor/requests');

    console.log("Approval Requests Data:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching approval requests:", error);
    throw error;
  }
};

// Get Approval Status
export const updateApprovalStatus = async (approvalRequestId, status) => {
  try {
    const response = await axiosInstance.put(`/api/approvals/requests/${approvalRequestId}/status`, {status});
    return response.data;
  } catch (error) {
    console.error("Error updating approval status:", error);
    throw error;
  }
}

export const rejectApprovalRequest = async (approvalRequestId) => {
  try {
    const response = await axiosInstance.post(`/api/approvals/requests/${approvalRequestId}/reject`);
    return response.data;
  } catch (error) {
    console.error("Error rejecting approval request:", error);
    throw error;
  }
};

export const createApprovalRequest = async (stepId, teamId) => {
  try {
    const response = await axiosInstance.post(`/api/approvals/requests`, {
      stepId,
      teamId,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating approval request:", error);
    throw error;
  }
};


// Courses & CourseSubscriptions
// Fetch all courses
export const fetchAllCourses = async () => {
  try {
    const response = await axiosInstance.get('/api/courses');
    return response.data;
  } catch (error) {
    console.error("Error fetching courses:", error);
    throw error;
  }
};

// Fetch courses by user ID
export const fetchCoursesByUserId = async (userId) => {
  try {
    const response = await axiosInstance.get(`/api/courses/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching courses by user ID:", error);
    throw error;
  }
};

// Fetch a specific course by ID
export const fetchCourseById = async (courseId) => {
  try {
    const response = await axiosInstance.get(`/api/courses/${courseId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching course:", error);
    throw error;
  }
};

// Create a new course
export const createCourse = async (courseData) => {
  try {
    const response = await axiosInstance.post('/api/courses', courseData);
    return response.data;
  } catch (error) {
    console.error("Error creating course:", error);
    throw error;
  }
};

// Update an existing course
export const updateCourse = async (courseId, courseData) => {
  try {
    const response = await axiosInstance.put(`/api/courses/${courseId}`, courseData);
    return response.data;
  } catch (error) {
    console.error("Error updating course:", error);
    throw error;
  }
};

// Delete a course
export const deleteCourse = async (courseId) => {
  try {
    const response = await axiosInstance.delete(`/api/courses/${courseId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting course:", error);
    throw error;
  }
};

// Fetch subscription details for order details page
export const fetchSubscriptionDetails = async (subscriptionId) => {
  try {
    console.log("Fetching subscription details for ID:", subscriptionId);
    const response = await axiosInstance.get(`/api/course-subscriptions/${subscriptionId}`);
    console.log("API Response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching subscription details:", error);
    console.error("Error response:", error.response?.data);
    console.error("Error status:", error.response?.status);
    throw error;
  }
};

// Subscribe to a course
export const subscribeToCourse = async (subscriptionData) => {
  try {
    const response = await axiosInstance.post('/api/course-subscriptions', subscriptionData);
    return response.data;
  } catch (error) {
    console.error("Error subscribing to course:", error);
    throw error;
  }
};

// Fetch all course subscriptions
export const fetchAllCourseSubscriptions = async () => {
  try {
    const response = await axiosInstance.get('/api/course-subscriptions');
    return response.data;
  } catch (error) {
    console.error("Error fetching course subscriptions:", error);
    throw error;
  }
};

export const checkSubscriptionEligibility = async (userIds) => {
  try {
    const response = await axiosInstance.post(`/api/course-subscriptions/check-eligibility`, { userIds });
    return response.data.eligibility; // Returns an array of eligibility statuses
  } catch (error) {
    console.error("Error checking subscription eligibility:", error);
    throw error;
  }
};

export const checkSubscriptionHistory = async (userIds) => {
  try {
    const response = await axiosInstance.post(`/api/course-subscriptions/check-subscription-history`, { userIds });
    return response.data.eligibility; // Returns an array of subscription history data
  } catch (error) {
    console.error("Error checking subscription history:", error);
    throw error;
  }
};

// Fetch remaining subscription time for a user
export const fetchRemainingSubscriptionTime = async (userId) => {
  try {
    const response = await axiosInstance.get(`/api/course-subscriptions/user/${userId}/remaining-time`);
    return response.data.subscriptions;
  } catch (error) {
    console.error("Error fetching remaining subscription time:", error);
    throw error;
  }
};

// Fetch a specific course subscription by ID
export const fetchCourseSubscriptionById = async (subscriptionId) => {
  try {
    const response = await axiosInstance.get(`/api/course-subscriptions/${subscriptionId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching course subscription:", error);
    throw error;
  }
};

export const fetchCoursesSubStatusByUserId = async (userId) => {
  try {
    const response = await axiosInstance.get(`/api/course-subscriptions/user/${userId}/status`);
    return response.data;
  } catch (error) {
    console.error("Error fetching courses with subscription status:", error);
    throw error;
  }
};

export const confirmSubscriptionPayment = async (subscriptionId, mpesaReferenceNumber) => {
  try {
      const response = await axiosInstance.post('/api/course-subscriptions/confirm', {
          subscriptionId,
          mpesaReferenceNumber
      });
      return response.data;
  } catch (error) {
      console.error("Error confirming subscription payment:", error);
      throw error;
  }
};

// Cancel a course subscription
export const cancelCourseSubscription = async (subscriptionId) => {
  try {
    const response = await axiosInstance.delete(`/api/course-subscriptions/${subscriptionId}`);
    return response.data;
  } catch (error) {
    console.error("Error canceling course subscription:", error);
    throw error;
  }
};

export const checkPoapEligibility = async (userId, courseId) => {
  try {
    const response = await axiosInstance.get(`/api/course-subscriptions/poap-eligibility/${userId}/${courseId}`);
    return response.data;
  } catch (error) {
    console.error("Error checking POAP eligibility:", error);

    // Check if the error is due to ineligibility (e.g., status code 403 or 400)
    if (error.response && (error.response.status === 403 || error.response.status === 400)) {
      // Return isEligible: false
      return { isEligible: false };
    } else {
      // Re-throw other errors (e.g., network issues, server errors)
      throw error;
    }
  }
};



// Payments
// Subscribe to a course and initiate payment (M-Pesa or WaafiPay)
export const subscribeAndPay = async ({ userId, courseId, referralCode, subscriptionType, phoneNumber, country }) => {
  try {
    const payload = {
      userId,
      courseId,
      referralCode,
      subscriptionType: subscriptionType || 'monthly',
      phoneNumber,
      country,
    };
    console.log('subscribeAndPay payload:', JSON.stringify(payload, null, 2));
    const response = await axiosInstance.post('/api/course-subscriptions/subscribe-and-pay', payload);
    return response.data;
  } catch (error) {
    console.error('subscribeAndPay error:', JSON.stringify(error.response?.data || error.message, null, 2));
    throw error.response?.data?.error || error.message;
  }
};

export const listenToPaymentStatus = (callback) => {
  const handler = (data) => {
    if (data && data.status) {
      console.log('Received payment status:', data);
      
      // Handle M-Pesa STK Push automatic confirmation
      if (data.status === 'PAID' && data.subscriptionStatus === 'active') {
        // Invalidate subscription status cache
        if (window.queryClient) {
          window.queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
          window.queryClient.invalidateQueries({ queryKey: ['courseSubscriptions'] });
          window.queryClient.invalidateQueries({ queryKey: ['courseStatuses'] });
        }
        
        // Show success notification
        console.log('M-Pesa payment confirmed automatically!');
      }
      
      // Handle payment failures
      if (data.status === 'FAILED') {
        console.log('Payment failed:', data.message);
      }
      
      callback(data);
    }
  };
  
  socket.on('paymentStatusUpdated', handler);
  return () => socket.off('paymentStatusUpdated', handler);
};

// Optional: Verify payment status
export const verifyPaymentStatus = async (billingId) => {
  try {
    const response = await axiosInstance.get(`/api/billings/${billingId}`);
    return response.data; // { status, transactionId, paymentDate, ... }
  } catch (error) {
    console.error('Error verifying payment status:', error);
    throw error.response?.data?.error || error.message;
  }
};



//Billing
// Fetch all billing records
export const fetchAllBillings = async () => {
  try {
    const response = await axiosInstance.get('/api/billings');
    return response.data;
  } catch (error) {
    console.error("Error fetching all billing records:", error);
    throw error;
  }
};

// Fetch billing records by user ID (with authentication)
export const fetchBillingsByUser = async (userId) => {
  try {
    const response = await axiosInstance.get(`/api/billings/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching billing records by user ID:", error);
    throw error;
  }
};

// Fetch authenticated user's own billings (recommended)
export const fetchMyBillings = async () => {
  try {
    const response = await axiosInstance.get('/api/billings/my-billings');
    return response.data;
  } catch (error) {
    console.error("Error fetching your billing records:", error);
    throw error;
  }
};

// Fetch billing records by user ID and orderId (with authentication)
export const fetchBillingsByUserId = async (userId, orderId) => {
  try {
    const endpoint = orderId
      ? `/api/billings/user/${userId}/billing/${orderId}`
      : `/api/billings/user/${userId}`;

    const response = await axiosInstance.get(endpoint);
    return response.data;
  } catch (error) {
    console.error("Error fetching billing records by user ID and order ID:", error);
    throw error;
  }
};

// Fetch a specific billing record by ID
export const fetchBillingById = async (billingId) => {
  try {
    const response = await axiosInstance.get(`/api/billings/${billingId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching billing record by ID:", error);
    throw error;
  }
};

// Delete a billing record by ID
export const deleteBillingById = async (billingId) => {
  try {
    const response = await axiosInstance.delete(`/api/billings/${billingId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting billing record by ID:", error);
    throw error;
  }
};


//Invoices
// Create a new invoice
export const createInvoice = async (invoiceData) => {
  try {
    const response = await axiosInstance.post('/api/invoices', invoiceData);
    return response.data;
  } catch (error) {
    console.error("Error creating invoice:", error);
    throw error;
  }
};

// Fetch all invoices
export const fetchAllInvoices = async () => {
  try {
    const response = await axiosInstance.get('/api/invoices');
    return response.data;
  } catch (error) {
    console.error("Error fetching all invoices:", error);
    throw error;
  }
};

// Fetch invoices by user ID
export const fetchInvoicesByUserId = async (userId) => {
  try {
    const response = await axiosInstance.get(`/api/invoices/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching invoices by user ID:", error);
    throw error;
  }
};

// Fetch invoice by ID
export const fetchInvoiceById = async (invoiceId) => {
  try {
    const response = await axiosInstance.get(`/api/invoices/${invoiceId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching invoice by ID:", error);
    throw error;
  }
};

// Delete an invoice by ID
export const deleteInvoiceById = async (invoiceId) => {
  try {
    const response = await axiosInstance.delete(`/api/invoices/${invoiceId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting invoice:", error);
    throw error;
  }
};


// Referral Code integration
export const fetchAllReferralCodes = async () => {
  try {
    const response = await axiosInstance.get('/api/referral-code/all');
    return response.data;
  } catch (error) {
    console.error("Error fetching all referral codes:", error);
    throw error;
  }
};

// Fetch referral code by UserID
export const fetchReferralCodesByUserId = async (userId) => {
  try {
    const response = await axiosInstance.get(`/api/referral-code/user/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching referral codes by User ID:", error);
    throw error;
  }
};

// Generate a referral code
export const generateReferralCode = async () => {
  try {
    const response = await axiosInstance.post('/api/referral-code/generate');
    return response.data;
  } catch (error) {
    console.error("Error generating referral code:", error);
    throw error;
  }
};

// Apply a referral code to get a discount on checkout
export const applyReferralCode = async (code, totalPrice, subscriptionType) => {
  try {
    const response = await axiosInstance.post('/api/referral-code/apply', { code, totalPrice, subscriptionType });
    return response.data;
  } catch (error) {
    console.error("Error applying referral code:", error);
    throw error;
  }
};

// Validate referral code without applying it
export const validateReferralCode = async (code, totalPrice, subscriptionType) => {
  try {
    const response = await axiosInstance.post(`/api/referral-code/validate`, { code, totalPrice, subscriptionType });
    return response.data;
  } catch (error) {
    console.error("Error validating referral code:", error);
    throw error;
  }
};

// Fetch all messages for teams assigned to a mentor
export const fetchMessagesForMentorTeams = async (mentorId) => {
  try {
    const response = await axiosInstance.get(`/api/messages/teamMessages/mentor/${mentorId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching messages for mentor's teams:", error);
    throw error;
  }
};

// Delete a specific team message
export const deleteTeamMessage = async (messageId) => {
  try {
    const response = await axiosInstance.delete(`/api/messages/teamMessages/${messageId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting team message:", error);
    throw error;
  }
};

//Timeline or Events

// Fetch all timelines by mentor ID
export const fetchTimelinesByMentor = async (mentorId) => {
  try {
    const response = await axiosInstance.get(`/api/timelines`, {
      params: { mentorId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching timelines by mentor:", error);
    return [];
  }
};

// Fetch timelines for the logged-in user's mentor
export const fetchUserTimelines = async (userId) => {
  try {
    const response = await axiosInstance.get(`/api/timelines/user/${userId}`);
    if (!response.data || response.data.length === 0) {
      console.warn("No timelines found for the user.");
      return [];
    }
    return response.data;
  } catch (error) {
    console.error("Error fetching timelines for user:", error.response?.data || error.message);
    throw new Error("Failed to fetch timelines. Please try again later.");
  }
};


// Create a new timeline
export const createTimeline = async (timelineData) => {
  try {
    const response = await axiosInstance.post(`/api/timelines`, timelineData);
    return response.data;
  } catch (error) {
    console.error("Error creating timeline:", error);
    throw error;
  }
};

// Create a new event
export const createEvent = async (eventData) => {
  try {
    const response = await axiosInstance.post(`/api/events`, eventData);
    return response.data;
  } catch (error) {
    console.error("Error creating event:", error);
    throw error;
  }
};

export const fetchUpcomingEvents = async (params = {}) => {
  try {
    // optional: pass filters like { days: 30 }
    const response = await axiosInstance.get(`/api/events/upcoming`, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching upcoming events:", error);
    throw error;
  }
};

// Update an existing timeline
export const updateTimeline = async (timelineId, timelineData) => {
  try {
    const response = await axiosInstance.put(`/api/timelines/${timelineId}`, timelineData);
    return response.data;
  } catch (error) {
    console.error("Error updating timeline:", error);
    throw error;
  }
};

// Delete a timeline
export const deleteTimeline = async (timelineId) => {
  try {
    const response = await axiosInstance.delete(`/api/timelines/${timelineId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting timeline:", error);
    throw error;
  }
};


// Fetch POAP claim information using the POAP name.
// This calls GET /poap-claim/claim-info/:poapName
export const getPoapClaimInfo = async (poapName) => {
  try {
    const response = await axiosInstance.get(`/api/poap-claim/claim-info/${encodeURIComponent(poapName)}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching POAP claim info:", error);
    throw error;
  }
};

// Claim a POAP using the provided address and POAP name.
// Expects a JSON body with { address, poapName }.
// This calls POST /poap-claim/claim
export const claimPoap = async ({ address, poapName }) => {
  try {
    const response = await axiosInstance.post("/api/poap-claim/claim", { address, poapName });
    return response.data;
  } catch (error) {
    console.error("Error claiming POAP:", error);
    throw error;
  }
};

export const checkPoapClaimed = async ({ address, poapName }) => {
  try {
    const response = await axiosInstance.get('/api/poap-claim/claimed', {
      params: { address, poapName }
    });
    return response.data;
  } catch (error) {
    console.error("Error checking POAP claim status:", error);
    throw error;
  }
};

// ➊ Add / update a phone number & send first code
export const addPhoneNumber = async (phoneNumber) => {
  try {
    const { data } = await axiosInstance.post('/api/users/add-phone', { phoneNumber });
    return data;
  } catch (error) {
    console.error('Error adding phone number:', error);
    throw error;
  }
};

// ➋ Verify the code the user just entered
export const verifyPhoneCode = async (code) => {
  try {
    const { data } = await axiosInstance.post('/api/users/verify-phone', { code });
    return data;
  } catch (error) {
    console.error('Error verifying phone code:', error);
    throw error;
  }
};

// ➌ Resend a new verification code
export const resendPhoneCode = async () => {
  try {
    const { data } = await axiosInstance.post('/api/users/resend-phone-code');
    return data;
  } catch (error) {
    console.error('Error resending phone code:', error);
    throw error;
  }
};

export const checkUsername = async (username) => {
  try {
    const response = await axiosInstance.get('/api/auth/check-username', {
      params: { username }
    });
    return response.data.available;
  } catch (error) {
    console.error("Error checking username availability:", error);
    // bubble up so callers can handle it (e.g. show a network error)
    throw error;
  }
};

// Pupil login for classroom access
export const pupilLogin = async (credentials) => {
  try {
    const response = await axiosInstance.post('/api/auth/pupil-login', credentials);
    return response.data;
  } catch (error) {
    console.error("Error during pupil login:", error);
    throw error;
  }
};

// Regular user login
export const userLogin = async (credentials) => {
  try {
    const response = await axiosInstance.post('/api/auth/login', credentials);
    return response.data;
  } catch (error) {
    console.error("Error during user login:", error);
    throw error;
  }
};

// Fetch available classrooms for students (public endpoint)
export const fetchAvailableClassrooms = async () => {
  try {
    const response = await axiosInstance.get('/api/classrooms/public');
    return response.data;
  } catch (error) {
    console.error("Error fetching available classrooms:", error);
    throw error;
  }
};




